import { CalendarItemState } from 'utils/calendarItems';
import { Context } from '@tock/coretypes';
import { SquareCoordinates } from 'actions/CalendarActions';
import { assertNever } from '@tock/shared/utils/typeUtils';

enum CalendarItemActions {
  BOOK_ON_MULTIPLE = 'BOOK_ON_MULTIPLE',
  CANCEL_EMAIL_HANDSELL = 'CANCEL_EMAIL_HANDSELL',
  CANCEL_HOLD_FOR = 'CANCEL_HOLD_FOR',
  CANCEL_PURCHASE = 'CANCEL_PURCHASE',
  COMP = 'COMP',
  DELETE = 'DELETE',
  EDIT = 'EDIT',
  EDIT_SOLD = 'EDIT_SOLD',
  HOLD_FOR = 'HOLD_FOR',
  MODIFY = 'MODIFY',
  MOVE = 'MOVE',
  PIN = 'PIN',
  REFUND = 'REFUND',
  ASSIGN_TABLE = 'ASSIGN_TABLE',
  RESCHEDULE = 'RESCHEDULE',
  RESEND_EMAIL_HANDSELL = 'RESEND_EMAIL_HANDSELL',
  SELECT = 'SELECT',
  SELL = 'SELL',
  SELL_BY_EMAIL = 'SELL_BY_EMAIL',
  SEND_TO_WAITLIST = 'SEND_TO_WAITLIST',
  TOGGLE = 'TOGGLE',
  TRANSFER = 'TRANSFER',
  TRANSFER_CANCEL = 'TRANSFER_CANCEL',
  UNPIN = 'UNPIN',
  VIEW_IN_GOOGLE = 'VIEW_IN_GOOGLE',
}

export default CalendarItemActions;

export interface CalendarItemPopoverFunctions {
  onOpenPopover(coordinates: SquareCoordinates): void;
  onClosePopover(type?: 'DEFAULT' | 'MENU_ONLY'): void;
}

export interface CalendarItemActionFunctions {
  onBookByEmail(): void;
  onBookNow(): void;
  onBookOnMultiple(): void;
  onCancel(): void;
  onCancelEmailHandSell(): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCancelHoldFor(): any;
  onComp(): void;
  onDelete(): void;
  onEdit(): void;
  onEditSold(): void;
  onHoldFor(): void;
  onMove(): void;
  onPin(): void;
  onAssignTable(): void;
  onReschedule(): void;
  onResendEmailHandSell(): void;
  onSendToWaitlist(): void;
  onShowBookingModal(): void;
  onToggleState(): void;
  onTransfer(): void;
  onTransferCancel(): void;
  onUnpin(): void;
  onViewInGoogle(): void;
}

// TODO(team-foh): It appears that this context: param is never actually used.
// If we are ever doing any active cleanup or changes that involve this part
// of dashboard, clean it up.
export interface CalendarItemActionProps extends CalendarItemActionFunctions {
  context: Context;
}

export interface SoldCalendarItemActionProps {
  onCancel(): void;
  onComp(): void;
  onEditSold(): void;
  onPin(): void;
  onAssignTable(): void;
  onReschedule(): void;
  onShowBookingModal(): void;
  onTransfer(): void;
  onTransferCancel(): void;
  onUnpin(): void;
  onViewInGoogle(): void;
}

interface SoldActionMapParams {
  type: 'SOLD';
  props: SoldCalendarItemActionProps;
}

interface ActionMapParams {
  type: 'ALL';
  props: CalendarItemActionProps;
}

function getActionMapForType(params: SoldActionMapParams | ActionMapParams) {
  switch (params.type) {
    case 'SOLD':
      return {
        [CalendarItemActions.CANCEL_PURCHASE]: params.props.onCancel,
        [CalendarItemActions.COMP]: params.props.onComp,
        [CalendarItemActions.EDIT_SOLD]: params.props.onEditSold,
        [CalendarItemActions.PIN]: params.props.onPin,
        [CalendarItemActions.ASSIGN_TABLE]: params.props.onAssignTable,
        [CalendarItemActions.RESCHEDULE]: params.props.onReschedule,
        [CalendarItemActions.SELECT]: params.props.onShowBookingModal,
        [CalendarItemActions.TRANSFER]: params.props.onTransfer,
        [CalendarItemActions.TRANSFER_CANCEL]: params.props.onTransferCancel,
        [CalendarItemActions.UNPIN]: params.props.onUnpin,
        [CalendarItemActions.VIEW_IN_GOOGLE]: params.props.onViewInGoogle,
      };
    case 'ALL':
      return {
        [CalendarItemActions.BOOK_ON_MULTIPLE]: params.props.onBookOnMultiple,
        [CalendarItemActions.CANCEL_EMAIL_HANDSELL]: params.props.onCancelEmailHandSell,
        [CalendarItemActions.CANCEL_HOLD_FOR]: params.props.onCancelHoldFor,
        [CalendarItemActions.CANCEL_PURCHASE]: params.props.onCancel,
        [CalendarItemActions.COMP]: params.props.onComp,
        [CalendarItemActions.DELETE]: params.props.onDelete,
        [CalendarItemActions.EDIT]: params.props.onEdit,
        [CalendarItemActions.EDIT_SOLD]: params.props.onEditSold,
        [CalendarItemActions.HOLD_FOR]: params.props.onHoldFor,
        [CalendarItemActions.MOVE]: params.props.onMove,
        [CalendarItemActions.PIN]: params.props.onPin,
        [CalendarItemActions.ASSIGN_TABLE]: params.props.onAssignTable,
        [CalendarItemActions.RESCHEDULE]: params.props.onReschedule,
        [CalendarItemActions.RESEND_EMAIL_HANDSELL]: params.props.onResendEmailHandSell,
        [CalendarItemActions.SELECT]: params.props.onShowBookingModal,
        [CalendarItemActions.SELL]: params.props.onBookNow,
        [CalendarItemActions.SELL_BY_EMAIL]: params.props.onBookByEmail,
        [CalendarItemActions.SEND_TO_WAITLIST]: params.props.onSendToWaitlist,
        [CalendarItemActions.TOGGLE]: params.props.onToggleState,
        [CalendarItemActions.TRANSFER]: params.props.onTransfer,
        [CalendarItemActions.TRANSFER_CANCEL]: params.props.onTransferCancel,
        [CalendarItemActions.UNPIN]: params.props.onUnpin,
        [CalendarItemActions.VIEW_IN_GOOGLE]: params.props.onViewInGoogle,
      };
    default:
      return assertNever(params);
  }
}

type FunctionForActionParams =
  | {
      actionKey: CalendarItemActions;
      props: CalendarItemActionProps;
      calendarItemType: CalendarItemState;
    }
  | {
      actionKey: CalendarItemActions;
      props: SoldCalendarItemActionProps;
      calendarItemType: 'SOLD';
    };

export function functionForAction(params: FunctionForActionParams) {
  let actionMap: {
    [K in CalendarItemActions]?: (() => void) | undefined;
  };
  if (params.calendarItemType === 'SOLD') {
    actionMap = getActionMapForType({
      type: 'SOLD',
      props: params.props,
    });
  } else {
    actionMap = getActionMapForType({
      type: 'ALL',
      props: params.props,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return actionMap[params.actionKey]!;
}
